// src/pages/Sites/SiteDetail.tsx
/**
 * Improvements in this version
 * - Clear "Back to Sites" button stays.
 * - Site Balance card: added KPI chips + a proper table shell (sticky header, zebra rows),
 *   fixed spacing, tabular numeric font, and consistent right-aligned numbers.
 * - Light helpers to format money/tons and handle missing values gracefully.
 * - Geofence section, quick export, centroid/area/perimeter analytics kept from prior version.
 *
 * Assumptions
 * - `site.balances` shape: { loads: number, tons: number, profit: number }
 * - ArcGIS polygon editor component exists at @/components/ArcGISPolygonEditor
 */

import AppShell from '@/components/AppShell'
import { Link, useParams } from 'react-router-dom'
import { fetchSites } from '@/lib/mockApi'
import { useEffect, useMemo, useState } from 'react'
import type { Site } from '@/lib/types'
import { get, set } from '@/lib/storage'
import { logAudit } from '@/lib/audit'
import { useNotifications } from '@/lib/notifications'
import ArcGISPolygonEditor from '@/components/ArcGISPolygonEditor'
import { ArrowLeft, Download, ExternalLink } from 'lucide-react'

const OVERRIDES_KEY = 'sites-overrides'

// ---------- small math/format helpers ----------
function centroid(coords: number[][]){
  if (!coords.length) return null
  let x=0,y=0; coords.forEach(([lat,lng])=>{ x+=lat; y+=lng })
  return [x/coords.length, y/coords.length] as [number,number]
}
// equirectangular polygon area, good enough for small sites
function areaAcres(coords: number[][]){
  if (coords.length < 3) return 0
  const R = 6378137
  const lat0 = coords.reduce((s,[lat])=>s+lat,0)/coords.length * Math.PI/180
  const xy = coords.map(([lat,lng])=>{
    const x = (lng*Math.PI/180) * Math.cos(lat0) * R
    const y = (lat*Math.PI/180) * R
    return [x,y]
  })
  let a = 0
  for (let i=0;i<xy.length;i++){
    const [x1,y1] = xy[i]; const [x2,y2] = xy[(i+1)%xy.length]
    a += x1*y2 - x2*y1
  }
  const m2 = Math.abs(a)/2
  return m2 * 0.000247105
}
function perimeterMiles(coords: number[][]){
  if (coords.length < 2) return 0
  const R = 6371000
  let d = 0
  const rad = (x:number)=>x*Math.PI/180
  for (let i=0;i<coords.length;i++){
    const [lat1,lng1] = coords[i]
    const [lat2,lng2] = coords[(i+1)%coords.length]
    const φ1 = rad(lat1), φ2 = rad(lat2)
    const Δφ = rad(lat2-lat1), Δλ = rad(lng2-lng1)
    const a = Math.sin(Δφ/2)**2 + Math.cos(φ1)*Math.cos(φ2)*Math.sin(Δλ/2)**2
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    d += R*c
  }
  return d * 0.000621371
}
function toGeoJSON(coords: number[][]) {
  const ring = coords.map(([lat,lng])=>[lng,lat])
  if (ring.length && (ring[0][0] !== ring[ring.length-1][0] || ring[0][1] !== ring[ring.length-1][1])) {
    ring.push([...ring[0]])
  }
  return {
    type: 'FeatureCollection',
    features: [{ type:'Feature', properties:{}, geometry:{ type:'Polygon', coordinates:[ring] } }]
  }
}
function download(text: string, filename: string, mime='application/json'){
  const blob = new Blob([text], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
const fm = (n:number)=>`$${(n||0).toFixed(2)}`
const ft = (n:number)=> (n||0).toLocaleString(undefined,{minimumFractionDigits:3, maximumFractionDigits:3})

export default function SiteDetail(){
  const { id } = useParams()
  const [sites, setSites] = useState<Site[]>([])
  const [overrides, setOverrides] = useState<Record<string,Partial<Site>>>(get(OVERRIDES_KEY, {}))
  const { push } = useNotifications()

  useEffect(()=>{ fetchSites().then(setSites) },[])

  const site = useMemo(()=> {
    const base = sites.find(s => s.id === id)
    const ov = overrides[id || ''] || {}
    return base ? ({...base, ...ov, geofence: (ov as any).geofence ?? (base as any).geofence}) : null
  }, [sites, id, overrides])

  function saveGeofence(coords: number[][]){
    if (!id) return
    const next = { ...overrides, [id]: { ...(overrides[id]||{}), geofence: { type:'Polygon' as const, coordinates: coords } } }
    setOverrides(next); set(OVERRIDES_KEY, next)
    push({ title:'Geofence updated' })
    logAudit({ action:'save-geofence', details:{ site:id, coords } }, 'Admin')
  }

  if(!site) return <AppShell title="Site"><div className="text-sm muted">Loading…</div></AppShell>

  const coords = ((site as any).geofence?.coordinates ?? []) as number[][]
  const c = centroid(coords)
  const acres = areaAcres(coords)
  const perim = perimeterMiles(coords)

  function handleExportGeoJSON(){
    if (!coords.length) return
    const gj = toGeoJSON(coords)
    download(JSON.stringify(gj, null, 2), `${site.name.replace(/\s+/g,'_')}.geojson`)
    logAudit({ action:'export-geofence-geojson', details:{ site:id } }, 'Admin')
  }

  // balances (mock values available from your API)
  const loads = site.balances?.loads ?? 0
  const tons  = site.balances?.tons ?? 0
  const profit = site.balances?.profit ?? 0
  const loadsOut = 0, tonsOut = 0, profitOut = 0

  return (
    <AppShell title={site.name}>
      {/* Back + quick actions */}
      <div className="mb-3 flex items-center justify-between">
        <Link to="/sites" className="btn btn-secondary">
          <ArrowLeft className="w-4 h-4" /> Back to Sites
        </Link>
        <div className="flex gap-2">
          {!!coords.length && (
            <button className="btn btn-secondary" onClick={handleExportGeoJSON} title="Export geofence as GeoJSON">
              <Download className="w-4 h-4" /> Export GeoJSON
            </button>
          )}
          {c && (
            <a
              className="btn btn-secondary"
              href={`https://www.google.com/maps?q=${c[0]},${c[1]}`}
              target="_blank"
              rel="noreferrer"
              title="Open centroid in Google Maps"
            >
              <ExternalLink className="w-4 h-4" /> Open in Maps
            </a>
          )}
        </div>
      </div>

      {/* top grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Site details */}
        <div className="card">
          <div className="card-header">Site Details</div>
          <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-xs muted">Name</div>
              <div>{site.name}</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-xs muted">Description</div>
              <div>{site.description || '—'}</div>
            </div>
          </div>
        </div>

        {/* Geofence editor */}
        <div className="card">
          <div className="card-header">Site Geofence (ArcGIS)</div>
          <div className="card-body grid gap-3">
            <ArcGISPolygonEditor
              coords={(coords as [number,number][]) }
              onChange={(updated)=>saveGeofence(updated)}
            />
            <div className="text-sm">
              {c ? (
                <>
                  Centroid&nbsp;<span className="font-mono">{c[0].toFixed(6)}, {c[1].toFixed(6)}</span>
                  &nbsp;•&nbsp;Area&nbsp;<span className="font-mono">{acres.toFixed(2)}</span> acres
                  &nbsp;•&nbsp;Perimeter&nbsp;<span className="font-mono">{perim.toFixed(2)}</span> mi
                </>
              ) : (
                <>No polygon yet.</>
              )}
            </div>
            <div className="text-xs muted">
              Tip: Draw or edit the polygon. Stored as <code>[lat,lng]</code> pairs in <code>site.geofence.coordinates</code>.
            </div>
          </div>
        </div>

        {/* Site Balance */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">Site Balance</div>

            {/* KPI chips */}
            <div className="px-4 pt-4 grid sm:grid-cols-3 gap-3">
              <div className="card p-3 bg-[var(--surface-1)]">
                <div className="text-xs muted">Net Loads</div>
                <div className="text-lg font-semibold tabular-nums">{loads.toLocaleString()}</div>
              </div>
              <div className="card p-3 bg-[var(--surface-1)]">
                <div className="text-xs muted">Net Tons</div>
                <div className="text-lg font-semibold tabular-nums">{ft(tons)}</div>
              </div>
              <div className="card p-3 bg-[var(--surface-1)]">
                <div className="text-xs muted">Profit</div>
                <div className="text-lg font-semibold tabular-nums">{fm(profit)}</div>
              </div>
            </div>

            {/* Table */}
            <div className="card-body">
              <div className="table-shell">
                <table className="w-full text-sm table-zebra table-sticky">
                  <colgroup>
                    <col />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '22%' }} />
                  </colgroup>
                  <thead className="bg-[var(--surface-2)]">
                    <tr>
                      <th className="text-left px-3 py-2">Amount</th>
                      <th className="text-right px-3 py-2">In</th>
                      <th className="text-right px-3 py-2">Out</th>
                      <th className="text-right px-3 py-2">Current Balance</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    <tr>
                      <td className="px-3 py-2">Amount</td>
                      <td className="px-3 py-2 text-right">{fm(0)}</td>
                      <td className="px-3 py-2 text-right">{fm(0)}</td>
                      <td className="px-3 py-2 text-right">{fm(0)}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">Loads</td>
                      <td className="px-3 py-2 text-right">{loads.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">{loadsOut.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">{(loads - loadsOut).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">Tons</td>
                      <td className="px-3 py-2 text-right">{ft(tons)}</td>
                      <td className="px-3 py-2 text-right">{ft(tonsOut)}</td>
                      <td className="px-3 py-2 text-right">{ft(tons - tonsOut)}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">Profit</td>
                      <td className="px-3 py-2 text-right">{fm(profit)}</td>
                      <td className="px-3 py-2 text-right">{fm(profitOut)}</td>
                      <td className="px-3 py-2 text-right">{fm(profit - profitOut)}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">Avg Cost Per Load</td>
                      <td className="px-3 py-2 text-right">—</td>
                      <td className="px-3 py-2 text-right">—</td>
                      <td className="px-3 py-2 text-right">{fm(0)}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">Avg Cost Per Unit</td>
                      <td className="px-3 py-2 text-right">—</td>
                      <td className="px-3 py-2 text-right">—</td>
                      <td className="px-3 py-2 text-right">{fm(0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="text-xs muted mt-2">
                Numbers are right-aligned, use tabular figures, and the header sticks when scrolling. Replace placeholder
                costs with real values as that data becomes available.
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  )
}
